// Our own flat, illustrated style, using the OpenMapTiles schema served by
// OpenFreeMap. Leaflet remains responsible for interaction and property layers.
export const MAP_ATTRIBUTION = '<a href="https://openfreemap.org/">OpenFreeMap</a> · <a href="https://www.openmaptiles.org/">© OpenMapTiles</a> · <a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>';

const classes = values => ['match', ['get', 'class'], values, true, false];
const source = layer => ({source: 'neighborhood', 'source-layer': layer});
const fill = (id, layer, color, filter) => ({id, type:'fill', ...source(layer), ...(filter ? {filter} : {}), paint:{'fill-color':color}});
const line = (id, layer, color, width, filter) => ({id, type:'line', ...source(layer), ...(filter ? {filter} : {}), layout:{'line-cap':'round','line-join':'round'}, paint:{'line-color':color,'line-width':width}});
const name = ['coalesce', ['get', 'name:en'], ['get', 'name_en'], ['get', 'name']];
const labels = (id, layer, minzoom, size, filter, placement='point') => ({
  id, type:'symbol', ...source(layer), minzoom, ...(filter ? {filter} : {}),
  layout:{'symbol-placement':placement,'text-field':name,'text-font':['Noto Sans Regular'],'text-size':size,'text-max-width':8,'text-padding':8},
  paint:{'text-color':'#5b6259','text-halo-color':'#fffaf0','text-halo-width':1.6,'text-halo-blur':0.4}
});

export function createIllustratedMapStyle() {
  const layers = [
    {id:'paper',type:'background',paint:{'background-color':'#d9cfb5'}},
    fill('neighborhoods','landuse','#cec3a8',classes(['residential','commercial','industrial'])),
    fill('meadows','landcover','#a8cb7c',classes(['grass','farmland'])),
    fill('woods','landcover','#7fba65',classes(['wood'])),
    fill('sand','landcover','#efe0b1',classes(['sand'])),
    fill('snow','landcover','#f4f3eb',classes(['ice'])),
    fill('parks','park','#83c45e'),
    fill('recreation','landuse','#98cb69',classes(['cemetery','recreation_ground'])),
    fill('playing-fields','landuse','#b8d891',classes(['pitch'])),
    fill('school-grounds','landuse','#e4d5ad',classes(['school','hospital'])),
    fill('water','water','#5bc7ef'),
    line('streams','waterway','#5bc7ef',['interpolate',['linear'],['zoom'],8,0.6,15,3]),
    line('shoreline','water','#48b6dc',1),
    {...fill('buildings','building','#bcad91'),minzoom:14,paint:{'fill-color':'#bcad91','fill-outline-color':'#aa9e87','fill-opacity':0.65}}
  ];
  // White roads with a quiet edge, rather than the usual orange/yellow highway
  // palette. Keep paths distinct, and omit tunnel clutter at neighborhood scale.
  const roadGroups = [
    ['paths',['path','track'],13,0.7,3],
    ['local',['minor','service'],11,0.8,15],
    ['connecting',['secondary','tertiary'],8,1.4,22],
    ['main',['primary','trunk'],6,2,28],
    ['highways',['motorway'],4,2.5,34]
  ];
  roadGroups.forEach(([id,values,minzoom,start,end]) => {
    const filter=['all',classes(values),['!=',['get','brunnel'],'tunnel']];
    const width=['interpolate',['exponential',1.25],['zoom'],minzoom,start,19,end];
    const edge=['interpolate',['exponential',1.25],['zoom'],minzoom,start+0.6,19,end+2.4];
    layers.push({...line(`${id}-edge`,'transportation','#b8b29f',edge,filter),minzoom});
    layers.push({...line(id,'transportation',id==='highways'?'#eeeae0':'#fffdf6',width,filter),minzoom});
  });
  layers.push(
    {...line('country-boundaries','boundary','#b0a78f',1,['<=',['get','admin_level'],2]),paint:{'line-color':'#b0a78f','line-width':1,'line-dasharray':[3,3]}},
    labels('water-names','water_name',10,12),
    labels('main-road-names','transportation_name',11,11,classes(['motorway','trunk','primary','secondary','tertiary']),'line'),
    labels('street-names','transportation_name',14,11,classes(['minor','service']),'line'),
    labels('neighborhood-names','place',12,12,classes(['suburb','quarter','neighbourhood'])),
    labels('town-names','place',7,13,classes(['town','village'])),
    labels('city-names','place',3,['interpolate',['linear'],['zoom'],3,12,11,18],classes(['city'])),
    labels('state-names','place',3,12,classes(['state'])),
    labels('country-names','place',1,14,classes(['country']))
  );
  return {
    version:8,name:'Agent Winds Illustrated Neighborhood',
    glyphs:'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources:{neighborhood:{type:'vector',url:'https://tiles.openfreemap.org/planet',attribution:MAP_ATTRIBUTION}},
    transition:{duration:0},layers
  };
}
